import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtSize, fmtTime, fileUrl } from "@/lib/api";
import { PageHeader, EmptyState, CommandStatusChip } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Package, Upload, ChevronDown, ChevronUp, Rocket, Download } from "lucide-react";

export default function Updates() {
  const [releases, setReleases] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [armRollout, setArmRollout] = useState(null);
  const [repoOpen, setRepoOpen] = useState(false);
  const [repoVersion, setRepoVersion] = useState("");
  const [repoNotes, setRepoNotes] = useState("");
  const [repoDetected, setRepoDetected] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [devStatus, setDevStatus] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [rolloutTenant, setRolloutTenant] = useState("");
  const fileInput = useRef(null);

  const load = useCallback(() => {
    api.get("/releases").then((r) => setReleases(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!expanded) return undefined;
    const fetchDev = () =>
      api.get(`/releases/${expanded}/devices`).then((r) => setDevStatus(r.data)).catch(() => {});
    fetchDev();
    const t = setInterval(fetchDev, 5000);
    return () => clearInterval(t);
  }, [expanded]);

  async function upload() {
    if (!file || !version.trim() || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version", version.trim());
      fd.append("notes", notes);
      await api.post("/releases", fd);
      toast.success(`Release ${version.trim()} hochgeladen`);
      setUploadOpen(false);
      setFile(null);
      setVersion("");
      setNotes("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function openRepoDialog() {
    setRepoNotes("");
    try {
      const { data: info } = await api.get("/releases/repo-info");
      if (!info.ok) {
        toast.error("pi/-Ordner nicht im Server-Repository gefunden");
        return;
      }
      setRepoDetected(info.agent_version || "");
      setRepoVersion(info.agent_version || "");
      setRepoOpen(true);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function createFromRepo() {
    if (!repoVersion.trim() || busy) return;
    setBusy(true);
    try {
      const { data: rel } = await api.post("/releases/from-repo", { version: repoVersion.trim(), notes: repoNotes });
      toast.success(`Release ${rel.version} aus Repo-Code erzeugt (${fmtSize(rel.size_bytes)}) — Rollout startest du per Button`);
      setRepoOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function rollout(r) {
    try {
      const { data } = await api.post(`/releases/${r.id}/rollout`, { tenant_id: rolloutTenant || null });
      const scope = rolloutTenant ? ` (Mandant: ${tenants.find((t) => t.id === rolloutTenant)?.name || "?"})` : "";
      toast.success(`Rollout von ${data.version} an ${data.enqueued} Gerät${data.enqueued === 1 ? "" : "e"}${scope} gestartet — Offline-Geräte holen es beim nächsten Start nach`);
      setArmRollout(null);
      setRolloutTenant("");
      setExpanded(r.id);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  return (
    <div data-testid="updates-page">
      <PageHeader title="Software-Updates" sub="OTA-Rollout für die Geräte-Flotte — Paket hochladen, dann gezielt ausrollen">
        <div className="flex gap-2">
          <button onClick={openRepoDialog} className="btn-ghost" data-testid="release-repo-open">
            <Package size={15} /> Aus Repo-Code erzeugen
          </button>
          <button onClick={() => setUploadOpen(true)} className="btn-primary" data-testid="release-upload-open">
            <Upload size={15} /> Release hochladen
          </button>
        </div>
      </PageHeader>

      <div className="panel px-4 py-3 mb-6" data-testid="updates-info">
        <p className="text-[11px] text-slate-300 leading-relaxed">
          <span className="text-cyan-400 font-mono">Paket-Format:</span> .tar.gz mit den Ordnern <span className="font-mono">agent/</span> und/oder{" "}
          <span className="font-mono">player/</span> (max. 50 MB, SHA256-geprüft). Der Rollout geht an alle gekoppelten Geräte — oder gezielt
          an einen Mandanten — und startet erst, wenn du ihn hier auslöst. Geräte melden Download → Installation → Erfolg zurück.
          Details: <span className="font-mono">pi/OTA.md</span> im Repository.
        </p>
      </div>

      {releases === null ? (
        <div className="font-mono text-xs text-cyan-400 animate-pulse" data-testid="releases-loading">Lade Releases…</div>
      ) : releases.length === 0 ? (
        <EmptyState text="Noch keine Releases — lade das erste Update-Paket hoch" testid="releases-empty" />
      ) : (
        <div className="space-y-4">
          {releases.map((r, i) => (
            <div key={r.id} className="panel p-5" data-testid={`release-card-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Package size={18} className="text-cyan-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-100" data-testid={`release-version-${i}`}>v{r.version}</div>
                    <div className="font-mono text-[10px] text-slate-400 mt-0.5">
                      {fmtTime(r.created_at)} · {fmtSize(r.size_bytes)} · sha256 {r.sha256?.slice(0, 12)}… · {r.created_by}
                    </div>
                    {r.notes && <div className="text-xs text-slate-400 mt-1.5">{r.notes}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {armRollout === r.id ? (
                    <>
                      <select
                        value={rolloutTenant}
                        onChange={(e) => setRolloutTenant(e.target.value)}
                        className="input-dark !w-auto !py-1.5 text-xs"
                        data-testid={`release-rollout-tenant-${i}`}
                      >
                        <option value="">Alle Mandanten</option>
                        {tenants.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <button onClick={() => { setArmRollout(null); setRolloutTenant(""); }} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`release-rollout-cancel-${i}`}>
                        Abbrechen
                      </button>
                      <button
                        onClick={() => rollout(r)}
                        className="inline-flex items-center gap-1.5 rounded-sm bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400"
                        data-testid={`release-rollout-confirm-${i}`}
                      >
                        <Rocket size={12} /> Jetzt ausrollen
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setArmRollout(r.id)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`release-rollout-${i}`}>
                      <Rocket size={13} /> Rollout starten
                    </button>
                  )}
                  <a
                    href={fileUrl(r.download_url)}
                    download
                    className="btn-ghost !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1.5"
                    title="Paket (.tar.gz) herunterladen"
                    data-testid={`release-download-${i}`}
                  >
                    <Download size={13} /> Paket
                  </a>
                  <button
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="btn-ghost !py-1.5 !px-2.5 text-xs"
                    data-testid={`release-devices-toggle-${i}`}
                  >
                    {expanded === r.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    Status
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4" data-testid={`release-stats-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Rollout:</span>
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-sm border border-border text-slate-400">
                  {r.rollout.total} Geräte
                </span>
                {r.rollout.pending > 0 && <CommandStatusChip status="PENDING" />}
                {r.rollout.pending > 0 && <span className="font-mono text-[10px] text-amber-400 -ml-1">{r.rollout.pending}</span>}
                {r.rollout.downloading + r.rollout.applying > 0 && <CommandStatusChip status="DOWNLOADING" />}
                {r.rollout.downloading + r.rollout.applying > 0 && (
                  <span className="font-mono text-[10px] text-blue-400 -ml-1">{r.rollout.downloading + r.rollout.applying}</span>
                )}
                {r.rollout.success > 0 && <CommandStatusChip status="SUCCESS" />}
                {r.rollout.success > 0 && <span className="font-mono text-[10px] text-emerald-400 -ml-1">{r.rollout.success}</span>}
                {r.rollout.failed > 0 && <CommandStatusChip status="FAILED" />}
                {r.rollout.failed > 0 && <span className="font-mono text-[10px] text-red-400 -ml-1">{r.rollout.failed}</span>}
                {r.rollout.total === 0 && <span className="font-mono text-[10px] text-slate-400">noch nicht ausgerollt</span>}
              </div>

              {expanded === r.id && (
                <div className="mt-4 border-t border-border pt-4" data-testid={`release-devices-table-${i}`}>
                  {devStatus.length === 0 ? (
                    <div className="font-mono text-xs text-slate-400 py-2">Noch kein Gerät im Rollout</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left">
                          <th className="mono-label font-normal py-2 pr-3">Gerät</th>
                          <th className="mono-label font-normal py-2 pr-3">Mandant</th>
                          <th className="mono-label font-normal py-2 pr-3">Status</th>
                          <th className="mono-label font-normal py-2 pr-3">Meldung</th>
                          <th className="mono-label font-normal py-2">Aktualisiert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devStatus.map((u) => (
                          <tr key={u.id} className="border-b border-border/50" data-testid={`release-device-row-${u.device_id.slice(0, 8)}`}>
                            <td className="py-2 pr-3 text-xs text-slate-200">{u.device_name}</td>
                            <td className="py-2 pr-3 text-xs text-slate-300">{u.tenant_name || "—"}</td>
                            <td className="py-2 pr-3"><CommandStatusChip status={u.status} /></td>
                            <td className="py-2 pr-3 font-mono text-[11px] text-slate-300 max-w-56 truncate" title={u.message}>{u.message || "—"}</td>
                            <td className="py-2 font-mono text-[11px] text-slate-400">{fmtTime(u.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="release-upload-dialog">
          <DialogTitle>Release hochladen</DialogTitle>
          <div className="space-y-4 mt-2">
            <div>
              <div className="mono-label mb-1.5">Update-Paket (.tar.gz)</div>
              <button onClick={() => fileInput.current?.click()} className="w-full border border-dashed border-border hover:border-cyan-500/50 rounded-sm p-5 text-center transition-colors" data-testid="release-file-drop">
                <Upload className="mx-auto text-cyan-400 mb-1.5" size={20} strokeWidth={1.5} />
                <div className="text-sm text-slate-300">{file ? file.name : "Paket auswählen"}</div>
                <div className="font-mono text-[10px] text-slate-400 mt-1">agent/ und/oder player/ im Wurzelverzeichnis</div>
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".tar.gz,.tgz,application/gzip"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="release-file-input"
              />
            </div>
            <div>
              <div className="mono-label mb-1.5">Version</div>
              <input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="z. B. 0.3.0"
                className="input-dark"
                data-testid="release-version-input"
              />
            </div>
            <div>
              <div className="mono-label mb-1.5">Notizen (optional)</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Was ändert sich?"
                className="input-dark resize-none"
                data-testid="release-notes-input"
              />
            </div>
            <button onClick={upload} disabled={!file || !version.trim() || busy} className="btn-primary w-full" data-testid="release-upload-submit">
              {busy ? "Lade hoch…" : "Hochladen (Rollout erst später manuell)"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={repoOpen} onOpenChange={setRepoOpen}>
        <DialogContent data-testid="release-repo-dialog">
          <DialogTitle>Release aus Repo-Code erzeugen</DialogTitle>
          <div className="space-y-4 mt-2">
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Der Server paketiert <span className="font-mono">pi/agent</span> + <span className="font-mono">pi/player</span>{" "}
              aus dem Repository (.tar.gz, SHA256) — kein manueller tar-Schritt mehr. Erkannt:{" "}
              <span className="font-mono text-cyan-300" data-testid="release-repo-detected">{repoDetected || "—"}</span>
            </p>
            <div>
              <div className="mono-label mb-1.5">Version</div>
              <input
                value={repoVersion}
                onChange={(e) => setRepoVersion(e.target.value)}
                className="input-dark"
                data-testid="release-repo-version-input"
              />
            </div>
            <div>
              <div className="mono-label mb-1.5">Notizen (optional)</div>
              <textarea
                value={repoNotes}
                onChange={(e) => setRepoNotes(e.target.value)}
                rows={2}
                className="input-dark resize-none"
                data-testid="release-repo-notes-input"
              />
            </div>
            <button onClick={createFromRepo} disabled={!repoVersion.trim() || busy} className="btn-primary w-full" data-testid="release-repo-submit">
              {busy ? "Paketiere…" : "Paket erzeugen (Rollout erst später manuell)"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
